<script setup lang="ts">
import Fuse from 'fuse.js'
import { PageState } from 'primevue/paginator'
import { Category, KnowledgeItem, Page, SubTopic, Topic } from '@/types/types'
import AppChips from '@/components/app/AppChips.vue'
import CardGrid from '@/components/layout/CardGrid.vue'
import AppHero from '@/components/app/AppHero.vue'
import CardKnowledgeBase from '@/components/card/CardKnowledgeBase.vue'
import AppChipsItem from '@/components/app/AppChipsItem.vue'
import SearchInput from '~/components/form/SearchInput.vue'
const { find, findOne } = useStrapi()

useSeoMeta({
  title: () => `Drivmedla - Kunskapsbank`,
  description: () =>
    'Här finns samlad information för dig som söker fakta kring omställning till fossilfritt. Alltifrån vägledning och mallar till inspirerande goda exempel.',
  ogTitle: () => `Drivmedla - Kunskapsbank`,
  ogDescription: () =>
    'Här finns samlad information för dig som söker fakta kring omställning till fossilfritt. Alltifrån vägledning och mallar till inspirerande goda exempel.',
  ogLocale: () => 'sv_SE',
  ogSiteName: () => 'Drivmedla',
  ogUrl: () => `${useRuntimeConfig().public.baseURL}${useRoute().fullPath}`,
  ogImage: () => `${useRuntimeConfig().public.baseURL}/_nuxt/assets/_logo-horizontal-GREEN.svg`,
})

const { data: pageInfo } = await useAsyncData(
  'knowledgebase-page',
  () => findOne<Page>('knowledgebase-page', { populate: '*' }),
  { transform: (_pageInfo) => _pageInfo.data }
)

const search = reactive<{ value: string }>({ value: '' })
const selectedFilters = reactive<{
  topics: string[]
  subTopics: string[]
  [key: string]: string[]
}>({
  topics: [],
  subTopics: [],
})
const filterAlphabeticalFalling = ref<boolean>(true)

const { data: knowledgeItems } = await useAsyncData(
  'knowledgebases',
  () =>
    find<KnowledgeItem>('knowledgebases', {
      populate: '*',
      pagination: { start: 0, limit: 20000 },
    }),
  { transform: (_knowledgeItems) => _knowledgeItems.data }
)

const { data: tagCategories } = await useAsyncData(
  'kb-tag-categories',
  () =>
    find<Category>('kb-tag-categories', { populate: '*', pagination: { start: 0, limit: 20000 } }),
  { transform: (_categories) => _categories.data }
)
tagCategories.value?.forEach((cat) => {
  selectedFilters[cat.attributes.name as keyof typeof selectedFilters] = []
})
const { data: topics } = await useAsyncData(
  'kb-topics',
  () => find<Topic>('kb-topics', { pagination: { start: 0, limit: 20000 } }),
  {
    transform: (_topics) => _topics.data,
  }
)

const { data: subTopics } = await useAsyncData(
  'kb-subtopics',
  () => find<SubTopic>('kb-subtopics', { pagination: { start: 0, limit: 20000 } }),
  { transform: (_subTopics) => _subTopics.data }
)

const getAllChips = computed(() => {
  return Object.values(selectedFilters).flat()
})

const clearAllChips = () => {
  for (const key in selectedFilters) {
    selectedFilters[key] = []
  }
}

const removeChip = (tag: string) => {
  for (const key in selectedFilters) {
    const index = selectedFilters[key].indexOf(tag)
    if (index !== -1) {
      selectedFilters[key].splice(index, 1)
      break
    }
  }
}

const filterAlphabetical = (filterByFalling: boolean) => {
  filterAlphabeticalFalling.value = filterByFalling
}

const getKnowledgeBaseTags = (knowledgebase: KnowledgeItem) => {
  let allTags = knowledgebase.tags?.data.map((tag) => tag.attributes.name) || []
  allTags = [...allTags]
  if (knowledgebase.topic.data?.attributes.name) {
    allTags.push(knowledgebase.topic.data?.attributes.name)
  }
  if (knowledgebase.subtopic.data?.attributes.name) {
    allTags.push(knowledgebase.subtopic.data?.attributes.name)
  }
  return allTags
}

const filteredItems = computed(() => {
  if (knowledgeItems.value) {
    let filtered = toRaw(knowledgeItems.value)

    filtered = filtered.sort((a, b) => {
      if (filterAlphabeticalFalling.value) {
        return a.attributes.title.localeCompare(b.attributes.title)
      } else {
        return b.attributes.title.localeCompare(a.attributes.title)
      }
    })

    if (search.value) {
      const fuse = new Fuse(filtered, {
        threshold: 0.3,
        keys: [
          'attributes.title',
          'attributes.description',
          ['attributes', 'tags', 'data', 'attributes', 'name'],
          ['attributes', 'topic', 'data', 'attributes', 'name'],
          ['attributes', 'subtopic', 'data', 'attributes', 'name'],
        ],
      })
      const result = fuse.search(search.value)
      const formattedResult = result.map((res) => res.item)
      filtered = [...formattedResult]
    }

    if (Object.values(selectedFilters).flat().length) {
      filtered = filtered.filter((item) => {
        let itemAttributes = item?.attributes.tags?.data.map((tag) => tag.attributes.name) || []
        itemAttributes = [
          ...itemAttributes,
          item.attributes.topic.data?.attributes.name,
          item.attributes.subtopic.data?.attributes.name,
        ]

        // This code will  use the logical OR (||) operator for filters selected within the topic and subtopic filtering.
        // And it will use the logical AND (&&) operator within the other filters in selectedFilters.
        // If the user for example would select multiple topics or subtopics, then an item would still show up if it has at least one of them.
        // But for the other filters it will use the logical AND operator resulting in the item only showing up if it has all the other selected filters.

        const topicFilters = selectedFilters.topics
        const subtopicFilters = selectedFilters.subTopics
        const otherFilters = Object.entries(selectedFilters)
          .filter(([key]) => key !== 'topics' && key !== 'subTopics')
          .map(([_, values]) => values)
          .flat()

        const hasAnyOfTopics =
          topicFilters.length === 0 || topicFilters.some((tag) => itemAttributes.includes(tag))
        const hasAnyOfSubtopics =
          subtopicFilters.length === 0 ||
          subtopicFilters.some((tag) => itemAttributes.includes(tag))
        const hasAllOtherFilters = otherFilters.every((tag) => itemAttributes.includes(tag))

        return hasAnyOfTopics && hasAnyOfSubtopics && hasAllOtherFilters
      })
    }

    return filtered
  }
})
const currentPageOptions = ref<PageState>({ first: 0, rows: 20, page: 0 })
const paginatedItems = computed(() => {
  return usePagination(filteredItems.value, currentPageOptions.value)
})

const updatePage = (e: PageState) => {
  currentPageOptions.value = e
}
</script>
<template>
  <main class="knowledgebank">
    <AppHero
      :title="pageInfo?.attributes.generalContent.title"
      :description="pageInfo?.attributes.generalContent.ingress"
      background-color="#F7E5BE"
      color="#000"
    />
    <LayoutSection>
      <div class="my-4">
        <SearchInput v-model:value="search.value" placeholder="Sök på sidan" />
      </div>
      <AppChips v-if="getAllChips.length" size="large" class="my-3">
        <AppChipsItem item="Rensa allt" :remove="() => clearAllChips()" type="clear" />
        <AppChipsItem
          v-for="chip in getAllChips"
          :key="chip"
          :item="chip"
          :remove="() => removeChip(chip)"
          type="selected"
        />
      </AppChips>
      <div class="knowledgebank__filter">
        <div class="knowledgebank__filter--select">
          <MultiSelect
            v-if="topics"
            v-model="selectedFilters.topics"
            :show-toggle-all="false"
            :options="(topics as unknown as Topic[])"
            option-label="attributes.name"
            option-value="attributes.name"
            option
            placeholder="Ämnesområde"
            class="w-full sm:w-auto"
            :max-selected-labels="2"
            selected-items-label="{0} valda"
          />
          <MultiSelect
            v-if="subTopics"
            v-model="selectedFilters.subTopics"
            :show-toggle-all="false"
            :options="(subTopics as unknown as SubTopic[])"
            option-label="attributes.name"
            option-value="attributes.name"
            option
            placeholder="Delområden"
            class="w-full sm:w-auto"
            :max-selected-labels="2"
            selected-items-label="{0} valda"
          />
          <MultiSelect
            v-for="category in tagCategories"
            :key="category.id"
            v-model="selectedFilters[category.attributes.name]"
            :show-toggle-all="false"
            :options="category.attributes.tags.data"
            :placeholder="category.attributes.name"
            option-label="attributes.name"
            option-value="attributes.name"
            class="w-full sm:w-auto"
            :max-selected-labels="2"
            selected-items-label="{0} valda"
          />
        </div>
        <div>
          <Button
            :label="filterAlphabeticalFalling ? 'A-Ö' : 'Ö-A'"
            plain
            text
            :icon="filterAlphabeticalFalling ? 'pi pi-arrow-down' : 'pi pi-arrow-up'"
            icon-pos="right"
            class="pr-0"
            @click="filterAlphabetical(!filterAlphabeticalFalling)"
          />
        </div>
      </div>
      <CardGrid v-if="paginatedItems.length" :grid="2">
        <CardKnowledgeBase
          v-for="knowledgebase in paginatedItems"
          :key="knowledgebase.id"
          :title="knowledgebase.attributes.title"
          :description="knowledgebase.attributes.description"
          :slug="knowledgebase.attributes.slug"
          :tags="getKnowledgeBaseTags(knowledgebase.attributes)"
        />
      </CardGrid>
      <div v-else class="flex align-items-center justify-content-center h-22rem">
        <AppHeading title="Inga inlägg hittades..." type="h2" size="large" />
      </div>
      <Paginator
        v-if="filteredItems && filteredItems.length > currentPageOptions.rows"
        :rows="currentPageOptions.rows"
        :total-records="filteredItems?.length"
        @page="updatePage($event)"
      />
    </LayoutSection>
  </main>
</template>

<style scoped lang="scss">
.knowledgebank {
  &__chips {
    display: flex;
    column-gap: 1rem;
  }

  &__filter {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    width: 100%;

    &--select {
      display: flex;
      flex-flow: row wrap;
      gap: 1rem;
    }
  }
}
</style>
